import { useCallback, useEffect, useRef, useState } from "react";
import { ChatMessage, ChatMessageRole } from "duck/ui/chat/types";
import {
  calculateSpacerHeight,
  isScrolledToBottom,
  scrollToMessage,
} from "duck/ui/chat/utils";

export const useChatMessagesScrolling = ({
  messages,
  isStreaming,
  currentThreadId,
  followupSuggestions,
}: {
  messages: ChatMessage[];
  isStreaming: boolean;
  currentThreadId: string | null;
  followupSuggestions: any[];
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mostRecentUserMessageRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  const [spacerHeight, setSpacerHeight] = useState(0);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const previousLastUserMessageIndexRef = useRef(-1);
  const spacerHeightRef = useRef(0);
  // Saved before any user interaction that could trigger a layout change (e.g.
  // clicking to focus/unfocus the input). We use capture-phase listeners so the
  // value is snapshotted before React processes the event and re-renders.
  const preInteractionScrollTopRef = useRef(0);

  const lastUserMessageIndex = messages.findLastIndex(
    (m) => m.role === ChatMessageRole.HUMAN
  );

  const handleScroll = useCallback(() => {
    setShowScrollToBottom(!isScrolledToBottom(containerRef, spacerHeight));
  }, [spacerHeight, containerRef]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);

      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [containerRef, spacerHeight, handleScroll]);

  // Scroll to the most recent user message ONLY when the user has just sent
  // a new message. We rely on isStreaming as the signal: sendMessage sets
  // isStreaming=true synchronously before the optimistic append, so only
  // user-initiated turns arrive here with isStreaming=true. Server
  // repopulations (initial load, thread switch, stream-end resync) always
  // run with isStreaming=false and are filtered out.
  useEffect(() => {
    const indexIncreased =
      lastUserMessageIndex > previousLastUserMessageIndexRef.current;
    const hasNewUserMessage = indexIncreased && isStreaming;

    if (hasNewUserMessage && lastUserMessageIndex >= 0) {
      scrollToMessage(containerRef, mostRecentUserMessageRef);
    }

    previousLastUserMessageIndexRef.current = lastUserMessageIndex;
  }, [lastUserMessageIndex, isStreaming]);

  useEffect(() => {
    handleScroll();
  }, [
    handleScroll,
    currentThreadId,
    followupSuggestions,
    isStreaming,
    messages,
  ]);

  // Whenever the messages or isStreaming state changes, we need to recalculate the spacer height
  useEffect(() => {
    const newHeight = calculateSpacerHeight(
      containerRef,
      mostRecentUserMessageRef,
      spacerRef
    );
    spacerHeightRef.current = newHeight;
    setSpacerHeight(newHeight);
  }, [messages, isStreaming, followupSuggestions, currentThreadId]);

  // Snapshot scrollTop before any user interaction that could collapse the input
  // and trigger a resize. Capture phase fires before React processes the event.
  useEffect(() => {
    const save = () => {
      if (containerRef.current) {
        preInteractionScrollTopRef.current = containerRef.current.scrollTop;
      }
    };
    document.addEventListener("pointerdown", save, { capture: true });
    document.addEventListener("keydown", save, { capture: true });

    return () => {
      document.removeEventListener("pointerdown", save, { capture: true });
      document.removeEventListener("keydown", save, { capture: true });
    };
  }, []);

  // Also recalculate the spacer when the container resizes (window resize,
  // sidebar toggle, mobile keyboard).
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      const currentScrollTop = container.scrollTop;
      const intermediateMax = container.scrollHeight - container.clientHeight;
      const wasAtIntermediateMax = currentScrollTop >= intermediateMax;

      const oldSpacerHeight = spacerHeightRef.current;
      const newHeight = calculateSpacerHeight(
        containerRef,
        mostRecentUserMessageRef,
        spacerRef
      );
      const delta = newHeight - oldSpacerHeight;

      spacerHeightRef.current = newHeight;

      // Imperatively update the spacer DOM so scrollHeight is correct when we
      // restore scrollTop below. Reading container.scrollHeight after the style
      // change forces a synchronous reflow (before paint), so both the spacer
      // update and the scrollTop correction land in the same frame — no flicker.
      if (spacerRef.current) {
        spacerRef.current.style.height = `${newHeight}px`;
      }

      // When the container grows the browser clamps scrollTop against the
      // intermediate (too-small) scrollHeight before the spacer is updated.
      // Restore the pre-interaction position so neither the clamping nor the
      // delta calculation shifts the user's view.
      if (delta > 0 && wasAtIntermediateMax) {
        const newMax = container.scrollHeight - container.clientHeight;
        container.scrollTop = Math.min(
          preInteractionScrollTopRef.current,
          newMax
        );
      }

      setSpacerHeight(newHeight);
    });
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  return {
    containerRef,
    mostRecentUserMessageRef,
    spacerRef,
    spacerHeight,
    lastUserMessageIndex,
    showScrollToBottom,
  };
};
